/**
 * Componente AppointmentCard
 * Tarjeta visual para mostrar una cita CRM
 * @module components/appointments/AppointmentCard
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import type { Appointment } from '@/types/appointment.types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  getModalityLabel,
  getModalityBadgeClasses,
  getModalityIcon,
} from '@/utils/appointmentHelpers';

interface AppointmentCardProps {
  appointment: Appointment;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  onClick,
  onEdit,
  onDelete,
  className = '',
}) => {
  const appointmentDate = new Date(appointment.scheduled_at);
  const timeLabel = formatDistanceToNow(appointmentDate, {
    addSuffix: true,
    locale: es,
  });

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Card
      className={`hover:shadow-md transition-shadow ${
        onClick ? 'cursor-pointer' : ''
      } ${className}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Header: Fecha, hora y modalidad */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {appointmentDate.toLocaleDateString('es-ES', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })}
              </p>
              <p className="text-xs text-gray-500">
                {appointmentDate.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getModalityBadgeClasses(
                appointment.modality
              )}`}
              title={getModalityLabel(appointment.modality)}
            >
              {(() => {
                const Icon = getModalityIcon(appointment.modality);
                return <Icon className="w-3.5 h-3.5" aria-hidden />;
              })()}
              <span className="hidden sm:inline">{getModalityLabel(appointment.modality)}</span>
            </span>
            <span className="text-xs text-gray-400">{timeLabel}</span>
          </div>
        </div>

        {/* Body: Información de la cita - Siempre mostrar contacto o "Sin contacto" (#7) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {appointment.contact_name ? (
              <span className="truncate text-gray-700">{appointment.contact_name}</span>
            ) : (
              <span className="text-red-600 font-medium" title="Cita sin contacto asociado">
                Sin contacto asignado
              </span>
            )}
          </div>

          {appointment.agent_name && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <UserIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">Agente: {appointment.agent_name}</span>
            </div>
          )}

          {appointment.delegation_name && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{appointment.delegation_name}</span>
            </div>
          )}

          {appointment.location && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{appointment.location}</span>
            </div>
          )}

          {appointment.notes && (
            <p className="text-xs text-gray-500 line-clamp-2 mt-2">
              {appointment.notes}
            </p>
          )}

          {/* Indicador resumen pendiente (cita completada sin resumen) */}
          {appointment.status === 'completed' && (
            <div className="flex items-center gap-2 mt-2">
              {appointment.summary?.trim() ? (
                <span className="inline-flex items-center gap-1 text-xs text-green-600" title="Tiene resumen">
                  <DocumentTextIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Resumen</span>
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded"
                  title="Cita completada sin resumen"
                >
                  <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                  <span>Sin resumen</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer: Duración y acciones */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <ClockIcon className="w-4 h-4" />
            <span>{appointment.duration} min</span>
          </div>

          {(onEdit || onDelete) && (
            <div className="flex items-center gap-2">
              {onEdit && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  Editar
                </Button>
              )}
              {onDelete && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Eliminar
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
